// Modal acessível via <dialog>. Foco fica preso (nativo do dialog showModal()).
//
// Fechamento tem duas alavancas independentes — clique no backdrop e tecla Esc —
// porque não são a mesma coisa: um fluxo com dados não confirmados precisa
// sobreviver ao clique perdido fora do painel, mas continuar respondendo ao Esc,
// que é ação deliberada e é o que o padrão WAI-ARIA de dialog espera. Uma prop só
// para os dois obrigava a escolher entre perder trabalho por acidente e prender
// quem navega por teclado.
//
// Fade in/out: ao fechar, marca data-state="closing" e adia o close() nativo até o
// fim da animação, mantendo o <dialog> no top-layer durante o fade de saída.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import styles from './Modal.module.css';

// Deve casar com --modal-fade-duration em tokens.css (180ms). O close() nativo é
// adiado por este intervalo pra o fade-out completar antes de sair do top-layer.
const FADE_MS = 180;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Atalho: quando false, nem backdrop nem Esc fecham. Default true. */
  dismissible?: boolean;
  /** Clique no backdrop fecha. Default: segue `dismissible`. */
  closeOnBackdrop?: boolean;
  /** Tecla Esc fecha. Default: segue `dismissible`. */
  closeOnEsc?: boolean;
  /** Largura máxima do modal. md=560 (default), lg=820, xl=1080. */
  size?: 'md' | 'lg' | 'xl';
}

export function Modal({
  open,
  onClose,
  title,
  children,
  dismissible = true,
  closeOnBackdrop,
  closeOnEsc,
  size = 'md',
}: ModalProps) {
  const backdropCloses = closeOnBackdrop ?? dismissible;
  const escCloses = closeOnEsc ?? dismissible;
  const dialogRef = useRef<HTMLDialogElement>(null);
  // id por instância: com um fixo, dois modais montados (um fechado) davam o mesmo
  // aria-labelledby e o leitor de tela anunciava o título do outro.
  const titleId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const clearCloseTimer = () => {
      if (closeTimer.current !== null) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };

    if (open) {
      // Reabrir cancela um fade-out em andamento.
      clearCloseTimer();
      if (!dialog.open) dialog.showModal();
      dialog.dataset.state = 'open';
    } else if (dialog.open) {
      // Fade-out: marca closing e adia o close() nativo até o fim da animação.
      dialog.dataset.state = 'closing';
      const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      closeTimer.current = setTimeout(
        () => {
          closeTimer.current = null;
          if (dialog.open) dialog.close();
        },
        reduce ? 0 : FADE_MS,
      );
    }

    return clearCloseTimer;
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const onCloseEvt = () => onClose();
    // Backdrop click: o alvo é o próprio <dialog> quando se clica fora do conteúdo.
    // Listener no DOM (não onClick JSX) — <dialog> não é "interativo" pro jsx-a11y.
    const onClick = (e: MouseEvent) => {
      if (!backdropCloses) return;
      if (e.target === dialog) onClose();
    };
    // ESC dispara o evento nativo `cancel` do <dialog>; bloqueia quando não fecha.
    const onCancel = (e: Event) => {
      if (!escCloses) e.preventDefault();
    };

    dialog.addEventListener('close', onCloseEvt);
    dialog.addEventListener('click', onClick);
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('close', onCloseEvt);
      dialog.removeEventListener('click', onClick);
      dialog.removeEventListener('cancel', onCancel);
    };
  }, [onClose, backdropCloses, escCloses]);

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${styles[size]}`}
      aria-labelledby={titleId}
    >
      <header className={styles.header}>
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Fechar modal">
          ×
        </button>
      </header>
      <div className={styles.body}>{children}</div>
    </dialog>
  );
}
